import React, { useState, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';
import { PlusIcon, TableIcon, XIcon, SaveIcon, CheckIcon, TrashIcon } from '@heroicons/react/outline';

import { Modal } from '../../design-system/components/feedback/Modal';
import { Input } from '../../design-system/components/inputs/Input';
import { TiptapEditor } from '../../common/TiptapEditor';
import { colors, spacing, typography } from '../../design-system/tokens';
import { useProject } from '../../contexts/ProjectContext';
import { researchTableService, ResearchTable } from '../../services/researchTableService';

// Types
interface ResourceStudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (title: string, content: any) => void;
  onDelete?: (resourceId: string) => Promise<void>;
  initialTitle?: string;
  initialContent?: any;
  resourceId?: string; // For editing existing resources
}

// Global styles to override modal defaults for template modal
const TemplateModalStyles = createGlobalStyle`
  .template-modal {
    background: ${colors.palette.gray[100]} !important;
    top: 2% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    height: 96% !important;
    max-height: 96% !important;
    
    /* Remove default modal body padding */
    & > div:last-child {
      padding: 0 !important;
    }
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TopPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${colors.palette.gray[25]};
  border-bottom: 1px solid rgba(0, 0, 0, 0.02);
`;

const TitleSection = styled.div`
  flex: 1;
  max-width: 300px;
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.text.primary};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing[1]};
  align-items: center;
`;

const SaveButton = styled.button`
  background: transparent;
  color: ${colors.text.primary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  background: transparent;
  color: ${colors.palette.red[600]};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.palette.red[50]};
    color: ${colors.palette.red[700]};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  background: ${colors.palette.gray[100]};
  padding: ${spacing[2]};
  display: flex;
  justify-content: center;
  overflow-y: auto;
`;

const ContentSheet = styled.div`
  width: 100%;
  max-width: 1200px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 600px;
  display: flex;
  padding: ${spacing[3]};
  gap: ${spacing[4]};
`;

const LeftPanel = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  border-right: 1px solid ${colors.border.secondary};
  padding-right: ${spacing[4]};
  max-height: 100%;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[6]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${spacing[4]};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding-bottom: ${spacing[2]};
  border-bottom: 1px solid ${colors.border.secondary};
  
  svg {
    width: 16px;
    height: 16px;
    color: ${colors.text.secondary};
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const Label = styled.label`
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
  margin: 0;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${colors.border.secondary};
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${colors.palette.indigo[400]};
    box-shadow: 0 0 0 3px ${colors.palette.indigo[100]};
  }
  
  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const EditorContainer = styled.div`
  flex: 1;
  background: ${colors.background.primary};
  height: 100%;
  
  .ProseMirror {
    padding: ${spacing[4]} !important;
    min-height: 500px !important;
    outline: none !important;
    border: none !important;
    font-size: 16px !important;
    line-height: 1.6 !important;
  }
`;

// Variables sidebar components
const SidebarTitle = styled.h3`
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin: 0 0 ${spacing[3]} 0;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  
  svg {
    width: 16px;
    height: 16px;
    color: ${colors.text.secondary};
  }
`;

const TableSection = styled.div`
  margin-bottom: ${spacing[4]};
`;

const TableName = styled.div`
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.secondary};
  margin-bottom: ${spacing[2]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const VariablesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const VariableItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[2]} ${spacing[3]};
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.primary};
  text-align: left;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.background.secondary};
  }
  
  &:active {
    background: ${colors.background.tertiary};
  }
`;

const VariableName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyButton = styled.div`
  opacity: 0;
  transition: opacity 0.15s ease;
  
  ${VariableItem}:hover & {
    opacity: 1;
  }
  
  svg {
    width: 12px;
    height: 12px;
    color: ${colors.text.secondary};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing[6]} ${spacing[4]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${spacing[4]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
`;

const VariablesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
  padding-right: ${spacing[1]};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.background.secondary};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.border.primary};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.text.tertiary};
  }
`;

interface ColumnVariable {
  id: string;
  name: string;
  tableName: string;
  tableId: string;
}

export const ResourceStudioModal: React.FC<ResourceStudioModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialTitle = '',
  initialContent,
  resourceId
}) => {
  const { selectedProject } = useProject();
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent || '');
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState<any>(null);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Variables sidebar state
  const [projectTables, setProjectTables] = useState<ResearchTable[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [availableVariables, setAvailableVariables] = useState<ColumnVariable[]>([]);

  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setContent(initialContent || '');
      
      fetchProjectTables();
    }
  }, [isOpen, initialTitle, initialContent, selectedProject, resourceId]);

  // Fetch project tables and extract columns
  const fetchProjectTables = async () => {
    if (!selectedProject) return;
    
    setColumnsLoading(true);
    try {
      const tables = await researchTableService.getResearchTables(selectedProject.project_uuid);
      setProjectTables(tables);
      
      // Extract all columns from all tables
      const variables: ColumnVariable[] = [];
      tables.forEach(table => {
        if (table.table_config?.columns) {
          table.table_config.columns.forEach(column => {
            variables.push({
              id: column.id,
              name: column.name,
              tableName: table.name,
              tableId: table.id
            });
          });
        }
      });
      
      setAvailableVariables(variables);
    } catch (error) {
      console.error('Error fetching project tables:', error);
    } finally {
      setColumnsLoading(false);
    }
  };

  // Set editor content when initialContent changes
  useEffect(() => {
    if (editor && initialContent) {
      if (typeof initialContent === 'object' && initialContent.type === 'doc') {
        // TipTap JSON content
        editor.commands.setContent(initialContent);
      } else if (typeof initialContent === 'string') {
        // Plain text content
        editor.commands.setContent(initialContent);
      }
    }
  }, [editor, initialContent]);

  const handleEditorChange = (newContent: any) => {
    setContent(newContent);
  };

  // Insert variable into editor
  const insertVariable = (variable: ColumnVariable) => {
    if (editor) {
      // Transform variable name: lowercase and replace spaces with underscores
      const transformedName = variable.name.toLowerCase().replace(/\s+/g, '_');
      const variableText = `{{${transformedName}}}`;
      editor.commands.insertContent(variableText);
    }
  };

  // Group variables by table
  const groupedVariables = projectTables.reduce((acc, table) => {
    const tableVariables = availableVariables.filter(v => v.tableId === table.id);
    if (tableVariables.length > 0) {
      acc[table.id] = {
        tableName: table.name,
        variables: tableVariables
      };
    }
    return acc;
  }, {} as Record<string, { tableName: string; variables: ColumnVariable[] }>);

  const handleSave = async () => {
    if (!title.trim()) {
      return;
    }
    
    setSaving(true);
    try {
      await onSave(title, content);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!resourceId || !onDelete) return;
    
    setDeleting(true);
    try {
      await onDelete(resourceId);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <TemplateModalStyles />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        height="full"
        showCloseButton={false}
        className="template-modal"
      >
        <ModalContent>
          <TopPanel>
            <TitleSection>
              <h3>{title || (resourceId ? 'Edit Template' : 'Create Template')}</h3>
            </TitleSection>

            <ActionButtons>
              <SecondaryButton
                onClick={onClose}
                disabled={saving || deleting}
              >
                <XIcon />
                Cancel
              </SecondaryButton>
              
              {resourceId && onDelete && (
                <DangerButton
                  onClick={handleDeleteClick}
                  disabled={saving || deleting}
                >
                  <TrashIcon />
                  Delete
                </DangerButton>
              )}
              
              <SaveButton
                onClick={handleSave}
                disabled={saving || deleting || !title.trim()}
              >
                {saving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <CheckIcon />
                    Save Template
                  </>
                )}
              </SaveButton>
            </ActionButtons>
          </TopPanel>

          <ContentArea>
          <ContentSheet>
            <LeftPanel>
              <SectionTitle>
                <TableIcon />
                Variables
              </SectionTitle>
              
              <VariablesContainer>
                {columnsLoading ? (
                  <LoadingState>Loading columns...</LoadingState>
                ) : Object.keys(groupedVariables).length === 0 ? (
                  <EmptyState>
                    No tables found in this project.
                    <br />
                    Create research tables to add variables.
                  </EmptyState>
                ) : (
                  Object.entries(groupedVariables).map(([tableId, { tableName, variables }]) => (
                    <TableSection key={tableId}>
                      <TableName>{tableName}</TableName>
                      <VariablesList>
                        {variables.map((variable) => (
                          <Tooltip 
                            key={variable.id} 
                            title={`Click to insert {{${variable.name.toLowerCase().replace(/\s+/g, '_')}}}`}
                            placement="right"
                          >
                            <VariableItem onClick={() => insertVariable(variable)}>
                              <VariableName>{variable.name}</VariableName>
                              <CopyButton>
                                <PlusIcon />
                              </CopyButton>
                            </VariableItem>
                          </Tooltip>
                        ))}
                      </VariablesList>
                    </TableSection>
                  ))
                )}
              </VariablesContainer>
            </LeftPanel>

            <RightPanel>
              <EditorContainer>
                <TiptapEditor
                  content={content}
                  onChange={handleEditorChange}
                  onEditorReady={setEditor}
                  placeholder="Start writing your template..."
                  editable={!saving}
                  minHeight={500}
                  toolbarPosition="top"
                />
              </EditorContainer>
            </RightPanel>
          </ContentSheet>
          </ContentArea>
        </ModalContent>
      </Modal>
      
      <Modal
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        title="Delete Template"
        size="sm"
      >
        <div style={{ padding: '16px 0' }}>
          <p>Are you sure you want to delete <strong>{title || 'this template'}</strong>?</p>
          <p style={{ fontSize: typography.fontSizes.sm, color: colors.text.secondary, margin: '8px 0 0 0' }}>
            This will permanently delete the template and all its content. This action cannot be undone.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: `1px solid ${colors.border.secondary}` }}>
          <Button onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button 
            type="primary" 
            danger 
            onClick={handleDeleteConfirm}
            loading={deleting}
          >
            {deleting ? "Deleting..." : "Delete Template"}
          </Button>
        </div>
      </Modal>
    </>
  );
};